import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/config";
import ProductCard from "@/components/ProductCard";
import NutritionInfographic from "@/components/NutritionInfographic";
import HayJourney from "@/components/HayJourney";
import SEO from "@/components/SEO";
import { Leaf, Sparkles, Sun, ShieldCheck, ArrowRight, Quote } from "lucide-react";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/aebe70cc-08e2-47fe-bdbd-71062b2d17e5/images/4c5b74c68e2e64e821e55b347ac71a1b2bb37ce62a91ae27d3a827187228bf62.png";
const FARM_IMG = "https://static.prod-images.emergentagent.com/jobs/aebe70cc-08e2-47fe-bdbd-71062b2d17e5/images/2b525a1c36c1b95766881e7dceb8681fa369dba13cef46a2e44a02d575b64724.png";
const GP_IMG = "https://images.unsplash.com/photo-1533152162573-93ad94eb20f6?crop=entropy&cs=srgb&fm=jpg&q=85";
const HAY_IMG = "https://static.prod-images.emergentagent.com/jobs/aebe70cc-08e2-47fe-bdbd-71062b2d17e5/images/3f1e2f8f9d62a25b83799f356157a0cb6a281309335a3ca9c58f6b69ef3715cd.png";
const TREAT_IMG = "https://static.prod-images.emergentagent.com/jobs/aebe70cc-08e2-47fe-bdbd-71062b2d17e5/images/7878304636aa27e490f229d90a5a0e43dbaed279b0b8e2e4ad3af0680c0a13e7.png";
const BEDDING_IMG = "https://images.unsplash.com/photo-1583337130417-3346a1be7dee?crop=entropy&cs=srgb&fm=jpg&q=85";
const FARM_SKETCH = "/hopsy-farm-0.webp";

const CATEGORY_TILES = [
  { id: "hay",       title: "Hay",       meta: "Timothy · Alfalfa · Meadow · Orchard", img: "/hopsy-cat-hay.webp" },
  { id: "pellets",   title: "Pellets",   meta: "Complete · Timothy · Alfalfa",          img: "/hopsy-cat-pellets.webp" },
  { id: "specialty", title: "Specialty", meta: "Bunny Bhel & more",                     img: "/hopsy-cat-specialty.webp" },
  { id: "bedding",   title: "Bedding",   meta: "Natural · Absorbent",                   img: "/hopsy-cat-bedding.webp" },
];

const VALUES = [
  { icon: Sun,         title: "100% Sun-Cured",   text: "Grown slowly, cured naturally. No shortcuts." },
  { icon: Leaf,        title: "Low-Dust Fibres",  text: "Screened & hand-selected for respiratory ease." },
  { icon: Sparkles,    title: "Dental-Kind",      text: "Long strands to wear teeth down the right way." },
  { icon: ShieldCheck, title: "Vet-Informed",     text: "Formulations guided by exotic pet expertise." },
];

const TESTIMONIALS = [
  { quote: "My two rabbits have never had healthier coats or calmer tummies. The Timothy hay is pristine — long strands, zero dust.", name: "Aanya R.", role: "Rabbit parent, Mumbai" },
  { quote: "Bunny Bhel is my guinea pigs' favourite treat. I love that it's fibre-first, not sugar-first.", name: "Rohan M.", role: "Cavvy parent, Bengaluru" },
  { quote: "The absorbent bedding actually works. My hutch smells fresh for days. Finely cut — very soft on feet.", name: "Priya S.", role: "Breeder, Pune" },
];

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const [posts, setPosts] = useState([]);
  const [ratings, setRatings] = useState({});

  useEffect(() => {
    api.get("/products", { params: { featured: true, _t: Date.now() } }).then((r) => setFeatured(r.data.slice(0, 4)));
    api.get("/blog").then((r) => setPosts(r.data.slice(0, 3)));
    api.get("/ratings-summary").then((r) => setRatings(r.data || {})).catch(() => {});
  }, []);

  return (
    <>
      <SEO
        title="Hopsy — Premium Hay, Pellets & Bedding for Small Pets (India)"
        description="Hopsy crafts 100% sun-cured hay, pellets and bedding for rabbits, guinea pigs, hamsters & chinchillas across India. Vet-informed, low-dust, naturally nutritious."
        path="/"
        keywords={[
          "small pet food india", "rabbit food india", "timothy hay india",
          "guinea pig hay india", "chinchilla hay india", "best hay for rabbits",
          "hay for rabbits india", "rabbit bedding india",
        ]}
      />
      {/* HERO */}
      <section className="relative overflow-hidden bg-cream" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-24 md:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-6 relative z-10 animate-fade-up">
              <h1 className="font-serif text-5xl md:text-7xl text-forest leading-[1.02] tracking-tightest mt-6">
                Slow-cured hay.
                <br />
                <span className="italic text-terra">Deeply</span> cared-for
                <br />
                little ones.
              </h1>
              <p className="mt-8 text-lg text-ink-muted max-w-lg font-light leading-relaxed">
                Hopsy crafts premium, 100% sun-cured hay, pellets and bedding for rabbits,
                guinea pigs, hamsters &amp; chinchillas — the way nature intended.
              </p>
              <div className="flex flex-wrap gap-4 mt-10">
                <Link
                  to="/shop"
                  data-testid="hero-shop-cta"
                  className="bg-forest text-cream px-8 py-4 text-xs uppercase tracking-widest hover:bg-forest-hover transition-colors inline-flex items-center gap-3"
                >
                  Shop Collection
                  <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                </Link>
                <Link
                  to="/about"
                  data-testid="hero-story-cta"
                  className="border border-forest text-forest px-8 py-4 text-xs uppercase tracking-widest hover:bg-forest hover:text-cream transition-colors"
                >
                  Our Story
                </Link>
              </div>

              <div className="mt-14 grid grid-cols-3 gap-4 max-w-md">
                <div>
                  <p className="font-serif text-3xl text-forest">9</p>
                  <p className="text-xs uppercase tracking-widest text-sage mt-1">SKUs crafted</p>
                </div>
                <div>
                  <p className="font-serif text-3xl text-forest">4</p>
                  <p className="text-xs uppercase tracking-widest text-sage mt-1">Species cared for</p>
                </div>
                <div>
                  <p className="font-serif text-3xl text-forest">100%</p>
                  <p className="text-xs uppercase tracking-widest text-sage mt-1">Sun-cured</p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-6 relative">
              <NutritionInfographic />
              <div className="hidden md:flex items-center gap-4 mt-2 px-2">
                <span className="h-px flex-1 bg-line"></span>
                <p className="font-serif italic text-base text-forest/80 text-center">
                  "We feed pets the way we'd feed family."
                </p>
                <span className="h-px flex-1 bg-line"></span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-20 md:py-28 bg-cream-200 relative grain" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
            {VALUES.map((v) => (
              <div key={v.title} className="border-l-2 border-forest pl-6">
                <v.icon className="w-6 h-6 text-terra mb-4" strokeWidth={1.5} />
                <h3 className="font-serif text-2xl text-forest leading-tight">{v.title}</h3>
                <p className="text-sm text-ink-muted mt-2 font-light leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="py-24 md:py-32" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-6">
            <div>
              <p className="tag-line">The Collection</p>
              <h2 className="font-serif text-4xl md:text-5xl text-forest mt-4 tracking-tightest">
                Four pillars of <span className="italic text-terra">complete care</span>.
              </h2>
            </div>
            <Link to="/shop" className="text-sm text-forest underline underline-offset-4 hover:text-terra">
              Browse all products →
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {CATEGORY_TILES.map((c) => (
              <Link
                key={c.id}
                to={`/shop?cat=${c.id}`}
                data-testid={`category-tile-${c.id}`}
                className="group relative flex flex-col border border-line hover:border-forest transition-colors duration-300 overflow-hidden"
              >
                <div className="relative aspect-[3/4] overflow-hidden bg-cream-200">
                  <img
                    src={c.img}
                    alt={c.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                    loading="lazy"
                  />
                </div>
                <div className="p-5 md:p-6 border-t border-line bg-cream">
                  <p className="text-[10px] uppercase tracking-widest text-sage">{c.meta}</p>
                  <div className="flex items-center justify-between mt-1">
                    <h3 className="font-serif text-3xl text-forest">{c.title}</h3>
                    <ArrowRight className="w-4 h-4 text-forest -translate-x-1 opacity-60 group-hover:translate-x-0 group-hover:opacity-100 transition-all" strokeWidth={1.5} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      {featured.length > 0 && (
        <section className="py-16 md:py-24 bg-cream-200" data-testid="featured-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="mb-12">
              <p className="tag-line">Editor's Selection</p>
              <h2 className="font-serif text-4xl md:text-5xl text-forest mt-4 tracking-tightest">Daily favourites.</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featured.map((p) => (<ProductCard key={p.slug} product={p} rating={ratings[p.slug]} />))}
            </div>
          </div>
        </section>
      )}

      {/* ABOUT TEASER */}
      <section className="py-24 md:py-32" data-testid="about-teaser">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-14 md:mb-16">
            <div className="lg:col-span-5">
              <p className="tag-line">Brand Story</p>
              <h2 className="font-serif text-4xl md:text-5xl text-forest mt-4 tracking-tightest leading-[1.05]">
                From meadow<br/>to mealtime.
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
              <p className="text-ink-muted font-light leading-relaxed">
                At the heart of Hopsy is a simple belief: small pets deserve the same
                quality of food and care as any member of the family. Six patient steps —
                each done by hand — from the meadow to your burrow.
              </p>
              <Link
                to="/about"
                className="mt-6 inline-flex items-center gap-3 text-forest hover:text-terra text-xs uppercase tracking-widest"
              >
                Read our full story
                <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            </div>
          </div>

          <HayJourney />
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 bg-forest text-cream relative grain" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <p className="tag-line" style={{ color: "#F3BE4A" }}>Kind Words</p>
          <h2 className="font-serif text-4xl md:text-5xl mt-4 tracking-tightest max-w-3xl">
            From the humans of very happy bunnies.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mt-16">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="border-t border-cream/20 pt-8">
                <Quote className="w-6 h-6 text-terra mb-5" strokeWidth={1.5} />
                <p className="font-serif text-xl leading-snug text-cream">"{t.quote}"</p>
                <div className="mt-6">
                  <p className="font-sans text-sm font-medium">{t.name}</p>
                  <p className="text-xs uppercase tracking-widest text-cream/60 mt-1">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BLOG TEASER */}
      {posts.length > 0 && (
        <section className="py-24 md:py-32" data-testid="journal-teaser">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-6">
              <div>
                <p className="tag-line">The Journal</p>
                <h2 className="font-serif text-4xl md:text-5xl text-forest mt-4 tracking-tightest">Pet care, considered.</h2>
              </div>
              <Link to="/blog" className="text-sm text-forest underline underline-offset-4 hover:text-terra">
                All articles →
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              {posts.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="group block" data-testid={`home-blog-${p.slug}`}>
                  <div className="aspect-[4/5] overflow-hidden bg-cream-200">
                    <img loading="lazy" src={p.cover_image} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="pt-5">
                    <p className="text-[10px] uppercase tracking-widest text-sage">{p.tag} · {p.read_time}</p>
                    <h3 className="font-serif text-2xl text-forest leading-snug mt-2 group-hover:text-terra transition">{p.title}</h3>
                    <p className="text-sm text-ink-muted mt-3 font-light line-clamp-2">{p.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA STRIP */}
      <section className="relative overflow-hidden bg-cream-200 py-20" data-testid="cta-strip">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div className="max-w-xl">
            <h2 className="font-serif text-4xl md:text-5xl text-forest tracking-tightest leading-tight">
              Give your small pet the <span className="italic text-terra">Hopsy difference</span>.
            </h2>
          </div>
          <div className="flex flex-wrap gap-4">
            <Link to="/shop" className="bg-forest text-cream px-8 py-4 text-xs uppercase tracking-widest hover:bg-forest-hover">
              Shop now
            </Link>
            <Link to="/contact" className="border border-forest text-forest px-8 py-4 text-xs uppercase tracking-widest hover:bg-forest hover:text-cream">
              Request wholesale
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
